import React, { useState } from 'react';
import axios from 'axios';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INITIAL_FORM = { name: '', email: '', message: '' };

const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="text-sm font-medium uppercase tracking-wider text-[var(--text-secondary)]">
    {children}
  </label>
);

const TextField = ({ id, type = "text", placeholder, value, onChange, testId }) => (
  <div className="space-y-2">
    <FieldLabel htmlFor={id}>{id}</FieldLabel>
    <Input
      id={id}
      name={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="bg-[var(--bg-page)] border-[var(--border-medium)] h-12 focus:border-[var(--brand-secondary)] transition-colors placeholder:text-[var(--text-muted)] text-[var(--text-primary)]"
      required
      data-testid={testId}
    />
  </div>
);

const submitContactForm = async (formData) => {
  await axios.post(`${API}/send-email`, formData);
};

export const ContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      toast.success("Message sent! I'll get back to you shortly.");
      setFormData(INITIAL_FORM);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-[var(--bg-card)] border border-[var(--border-medium)] p-8 md:p-16 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--brand-primary)] opacity-5 blur-[80px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[var(--brand-secondary)] opacity-5 blur-[80px] rounded-full pointer-events-none" />

      <div className="text-center mb-12 relative z-10">
        <h2 className="heading-2 mb-4">Let's Talk</h2>
        <p className="text-[var(--text-secondary)] text-xl">Reach out today for a free consultation.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 relative z-10" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextField id="name" placeholder="YOUR NAME" value={formData.name} onChange={handleInputChange} testId="contact-form-name-input" />
          <TextField id="email" type="email" placeholder="YOUR@EMAIL.COM" value={formData.email} onChange={handleInputChange} testId="contact-form-email-input" />
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="message">Message</FieldLabel>
          <Textarea
            id="message"
            name="message"
            placeholder="TELL ME ABOUT YOUR PROJECT..."
            value={formData.message}
            onChange={handleInputChange}
            className="bg-[var(--bg-page)] border-[var(--border-medium)] min-h-[150px] focus:border-[var(--brand-secondary)] transition-colors placeholder:text-[var(--text-muted)] text-[var(--text-primary)]"
            required
            data-testid="contact-form-message-input"
          />
        </div>

        <div className="pt-4 text-center">
          <button type="submit" disabled={isSubmitting} className="btn-primary w-full md:w-auto min-w-[200px]" data-testid="contact-form-submit-button">
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </div>
      </form>

      <div className="mt-12 pt-8 border-t border-[var(--border-medium)] text-center">
        <div className="flex items-center justify-center gap-2 text-[var(--text-secondary)]">
          <Mail className="w-5 h-5 text-[var(--brand-secondary)]" />
          <a href="mailto:digitalleaderproject@gmail.com" className="hover:text-[var(--brand-secondary)] transition-colors" data-testid="contact-form-email-link">
            digitalleaderproject@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
};
